<template>
  <el-card
    shadow="never"
    class="flex h-full min-h-[300px] flex-col"
    :body-style="{
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
      minHeight: '0',
    }">
    <template #header>
      <div class="flex items-center justify-between">
        <h3 class="font-medium">{{ i18n.t('browserExtension.topTools') }}</h3>
      </div>
    </template>

    <div v-if="loading" class="flex h-full items-center justify-center">
      <SmSkeleton height="200px" width="100%" />
    </div>

    <div
      v-else-if="!chartData.labels.length"
      class="flex h-full items-center justify-center text-gray-400">
      {{ i18n.t('noData') }}
    </div>

    <div v-else class="relative min-h-0 w-full flex-1">
      <div class="absolute inset-0">
        <BarChart
          :chart-data="chartData"
          :options="chartOptions"
          class="h-full w-full" />
      </div>
    </div>
  </el-card>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { BarChart } from 'vue-chart-3'
  import {
    Chart,
    BarController,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    TooltipItem,
  } from 'chart.js'
  import type { UsageStats } from '@/client'
  import { getToolsStatsByDeployment } from '@/stores/browserextension/browserextension.store'
  import { CHART_COLORS } from '@/stores/browserextension/constants'

  // Register Chart.js components
  Chart.register(BarController, CategoryScale, LinearScale, BarElement, Tooltip)

  const props = defineProps<{
    deploymentId?: string | null
  }>()

  const i18n = useI18n()
  const loading = ref(true)
  const tools = ref<UsageStats[]>([])

  // Track current request to prevent stale data updates
  let currentRequestId = 0
  // Track if component is mounted to prevent updates after unmount
  let isMounted = true

  async function loadData() {
    const requestId = ++currentRequestId

    try {
      loading.value = true
      // Fetch stats
      const result = await getToolsStatsByDeployment(props.deploymentId || null)

      // Only update if this is still the current request and component is mounted
      if (requestId === currentRequestId && isMounted) {
        tools.value = result
      }
    } catch (e) {
      console.error('Failed to load top tools', e)
    } finally {
      if (requestId === currentRequestId && isMounted) {
        loading.value = false
      }
    }
  }

  onMounted(() => {
    loadData()
  })

  onUnmounted(() => {
    isMounted = false
  })

  watch(
    () => props.deploymentId,
    () => {
      loadData()
    }
  )

  const topTools = computed(() => {
    // Backend sorts by total_sessions, but let's be safe and slice top 5
    return tools.value.slice(0, 5)
  })

  const chartData = computed(() => {
    const labels = topTools.value.map((t) => t.id || i18n.t('unknown'))
    const data = topTools.value.map((t) => t.metadata.total_sessions)

    return {
      labels,
      datasets: [
        {
          label: i18n.t('browserExtension.sessions'),
          data,
          backgroundColor: CHART_COLORS.primary,
          borderRadius: 4,
        },
      ],
    }
  })

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'bar'>) =>
            `${context.parsed.y} ${i18n.t('browserExtension.sessions')}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          drawBorder: false,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
      },
    },
  }
</script>
